// Sistema de Medalhas Regionalizadas por Município e Estado

export interface RegionalBadge {
  id: string
  name: string
  icon: string
  color: string
  description: string
  criteria: string
  points: number
  scope: 'municipal' | 'state' | 'national'
}

export const REGIONAL_BADGES: Record<string, RegionalBadge> = {
  // Medalhas Municipais
  guardian_city: {
    id: 'guardian_city',
    name: 'Guardião de {city}',
    icon: '🥉',
    color: 'orange',
    description: 'Você é um guardião da sua cidade!',
    criteria: '3 doações no município',
    points: 150,
    scope: 'municipal'
  },
  hero_city: {
    id: 'hero_city',
    name: 'Herói de {city}',
    icon: '🥈',
    color: 'gray',
    description: 'Sua cidade reconhece seu heroísmo!',
    criteria: '10 doações no município',
    points: 500,
    scope: 'municipal'
  },
  legend_city: {
    id: 'legend_city',
    name: 'Lenda de {city}',
    icon: '🥇',
    color: 'yellow',
    description: 'Você é uma lenda viva em sua cidade!',
    criteria: '25 doações no município',
    points: 1250,
    scope: 'municipal'
  },
  first_donor_city: {
    id: 'first_donor_city',
    name: 'Pioneiro de {city}',
    icon: '🌟',
    color: 'blue',
    description: 'Você foi um dos primeiros doadores da sua cidade!',
    criteria: 'Entre os 10 primeiros doadores do município',
    points: 200,
    scope: 'municipal'
  },
  
  // Medalhas Estaduais
  pride_state: {
    id: 'pride_state',
    name: 'Orgulho de {state}',
    icon: '🏆',
    color: 'purple',
    description: 'Todo o estado reconhece sua dedicação!',
    criteria: 'Top 10 doadores do estado',
    points: 1000,
    scope: 'state'
  },
  champion_state: {
    id: 'champion_state',
    name: 'Campeão de {state}',
    icon: '👑',
    color: 'gold',
    description: 'Você é o campeão estadual de doações!',
    criteria: '1º lugar no ranking estadual',
    points: 2000,
    scope: 'state'
  },
  top5_state: {
    id: 'top5_state',
    name: 'Top 5 de {state}',
    icon: '⭐',
    color: 'yellow',
    description: 'Você está entre os 5 maiores doadores do estado!',
    criteria: 'Top 5 no ranking estadual',
    points: 1500,
    scope: 'state'
  },
  
  // Medalhas Nacionais
  brazil_star: {
    id: 'brazil_star',
    name: 'Estrela do Brasil',
    icon: '⭐',
    color: 'green',
    description: 'Você brilha em todo o país!',
    criteria: 'Top 50 doadores nacionais',
    points: 3000,
    scope: 'national'
  },
  hall_of_fame: {
    id: 'hall_of_fame',
    name: 'Hall da Fama Nacional',
    icon: '🏅',
    color: 'gold',
    description: 'Seu nome está eternizado na história!',
    criteria: 'Top 10 doadores nacionais',
    points: 5000,
    scope: 'national'
  },
  national_legend: {
    id: 'national_legend',
    name: 'Lenda Nacional',
    icon: '👑',
    color: 'rainbow',
    description: 'Você é referência nacional em doação de sangue!',
    criteria: '1º lugar no ranking nacional',
    points: 10000,
    scope: 'national'
  },
  
  // Medalhas Especiais Regionalizadas
  critical_helper_city: {
    id: 'critical_helper_city',
    name: 'Resposta Rápida {city}',
    icon: '⚡',
    color: 'red',
    description: 'Você socorre sua cidade nos momentos críticos!',
    criteria: '5 doações em casos críticos do município',
    points: 750,
    scope: 'municipal'
  },
  referral_master_city: {
    id: 'referral_master_city',
    name: 'Embaixador de {city}',
    icon: '🤝',
    color: 'blue',
    description: 'Você trouxe novos heróis para sua cidade!',
    criteria: 'Referir 5 novos doadores do município',
    points: 500,
    scope: 'municipal'
  }
}

export function getBadgeForCity(badgeId: string, cityName: string): RegionalBadge {
  const badge = REGIONAL_BADGES[badgeId]
  if (!badge) return REGIONAL_BADGES.guardian_city
  
  return {
    ...badge,
    name: badge.name.replace('{city}', cityName)
  }
}

export function getBadgeForState(badgeId: string, stateName: string): RegionalBadge {
  const badge = REGIONAL_BADGES[badgeId]
  if (!badge) return REGIONAL_BADGES.pride_state
  
  return {
    ...badge,
    name: badge.name.replace('{state}', stateName)
  }
}

// Função para calcular medalhas baseado em estatísticas
export function calculateRegionalBadges(
  totalDonations: number,
  cityRank: number,
  stateRank: number,
  nationalRank: number,
  criticalDonations: number,
  referrals: number
): string[] {
  const badges: string[] = []
  
  // Medalhas Municipais
  if (totalDonations >= 25) badges.push('legend_city')
  else if (totalDonations >= 10) badges.push('hero_city')
  else if (totalDonations >= 3) badges.push('guardian_city')
  
  if (cityRank <= 10) badges.push('first_donor_city')
  if (criticalDonations >= 5) badges.push('critical_helper_city')
  if (referrals >= 5) badges.push('referral_master_city')
  
  // Medalhas Estaduais
  if (stateRank === 1) badges.push('champion_state')
  else if (stateRank <= 5) badges.push('top5_state')
  else if (stateRank <= 10) badges.push('pride_state')
  
  // Medalhas Nacionais
  if (nationalRank === 1) badges.push('national_legend')
  else if (nationalRank <= 10) badges.push('hall_of_fame')
  else if (nationalRank <= 50) badges.push('brazil_star')
  
  return badges
}

// Sistema de pontos ajustado
export const POINTS_SYSTEM = {
  donation_confirmed: 100,
  first_donation_city: 50,
  donation_sequence_3: 150, // 3 doações em 90 dias
  referral_same_city: 75,
  critical_donation_city: 200, // Doação em município com escassez crítica
  badge_earned: 50, // Bônus por cada medalha conquistada
  rank_improvement: 25 // Subir de posição no ranking
}
